/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useState, useEffect, useReducer, useCallback } from 'react';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';
import { isStrEmptyOrNull } from '../../common/UtilityFunctions';

function reducer(state, action) {
  switch (action.type) {
    case 'changed_paymentInfo': {
      return {
        ...action.paymentInfo,
      };
    }
    case 'changed_property': {
      return {
        ...state,
        ...action.property,
      };
    }
  }
  throw Error('Unkown action: ' + action.type);
}

const useGetPaymentInfo = (loanNumber, dueDate = null) => {
  const { get } = useAbortController();
  const { token } = useAuthContext();

  const [paymentInfo, dispatchPaymentInfo] = useReducer(reducer, {
    dueDate: null,
    apparPi: 0,
    totalEscrowShortage: 0,
    escrowShortageMonthlyRepaymentAmt: 0,
    escrowShortageRepayMonths: 0,
    lateChrgAcrd: 0,
    statementDate: null,
  });
  const [paymentStatus, setPaymentStatus] = useState('done');

  const getPaymentInfo = useCallback(
    async (date: string = null, params: PaymentInitReq = null, type = 'get'): Promise<LoanPayment> => {
      if (!token || !loanNumber || (!dueDate && !date)) return;
      const controller = get();
      const config = { signal: controller.signal };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const dateToUse = date === null ? dueDate : date;

      let req: any = {
        url: process.env.REACT_APP_PAYMENT_DUEDATE.replace('?', loanNumber) + dateToUse,
        method: type,
      };
      if (type === 'post') {
        req = {
          ...req,
          data: params,
        };
      }

      try {
        setPaymentStatus('loading');
        const response = await axiosCall(req, config, headers);
        dispatchPaymentInfo({ type: 'changed_paymentInfo', paymentInfo: response });
        setPaymentStatus('done');
        return response;
      } catch (e) {
        if (e.code !== 'ERR_CANCELED') {
          console.error('Unable to load payment info: ' + e);
          setPaymentStatus('error');
        }
      }
    },
    [get, loanNumber, dueDate, token],
  );

  useEffect(() => {
    getPaymentInfo();
  }, [getPaymentInfo]);

  const loadPayment = useCallback(
    async (date: string) => {
      if (!isStrEmptyOrNull(loanNumber) && !isStrEmptyOrNull(date) && date.match('^[0-9]{8}')) {
        const res = await getPaymentInfo(date);
        return res;
      }
      return null;
    },
    [getPaymentInfo, loanNumber],
  );

  const updatePayment = useCallback(
    async (date: string, params: PaymentInitReq) => {
      if (!isStrEmptyOrNull(loanNumber) && !isStrEmptyOrNull(date) && date.match('^[0-9]{8}')) {
        const res = await getPaymentInfo(date, params, 'post');
        return res;
      }
      return null;
    },
    [getPaymentInfo, loanNumber],
  );

  return {
    paymentInfo,
    paymentStatus,
    dispatchPaymentInfo,
    loadPayment,
    updatePayment,
    getPaymentInfo,
  };
};

export default useGetPaymentInfo;
